import logger from "@logger";
import Container from "@shared/components/Container";
import { Camera } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import useUploadProfilePhoto from "../mutations/usePhotoUpload";
import type { PhotoUploadFieldProps } from "../types";
import { compressImage } from "../utils/imageCompression";
import { PhotoCropModal } from "./PhotoCropModal";

export function PhotoUploadField({
  currentPhotoUrl,
  emptyStateIcon,
  onUploadComplete,
  onUploadError,
}: PhotoUploadFieldProps) {
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedImageUrlRef = useRef<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionError, setCompressionError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { mutate: uploadPhoto, isPending, isError, error, reset } = useUploadProfilePhoto();

  useEffect(() => {
    if (croppedImageBlob) {
      if (croppedImageBlob.size === 0) {
        logger.error("Cannot create preview: blob is empty");
        setPreviewUrl(null);
        return undefined;
      }

      const url = URL.createObjectURL(croppedImageBlob);
      setPreviewUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setPreviewUrl(null);
    return undefined;
  }, [croppedImageBlob]);

  const revokeSelectedImageUrl = () => {
    if (selectedImageUrlRef.current) {
      URL.revokeObjectURL(selectedImageUrlRef.current);
      selectedImageUrlRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (selectedImageUrlRef.current) {
        URL.revokeObjectURL(selectedImageUrlRef.current);
      }
    };
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      logger.error("Please select an image file");
      return;
    }

    setCroppedImageBlob(null);
    setCompressionError(null);
    setUploadError(null);
    reset();

    revokeSelectedImageUrl();
    const objectUrl = URL.createObjectURL(file);
    selectedImageUrlRef.current = objectUrl;
    setSelectedImage(objectUrl);
    setShowCropModal(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    if (croppedBlob.size === 0) {
      logger.error("Cropped blob is empty");
      return;
    }

    setShowCropModal(false);
    revokeSelectedImageUrl();
    setCompressionError(null);
    setUploadError(null);
    setIsCompressing(true);

    try {
      const croppedFile = new File([croppedBlob], "cropped.jpg", { type: croppedBlob.type || "image/jpeg" });
      const compressedBlob = await compressImage(croppedFile, 500, 0.85);
      setCroppedImageBlob(compressedBlob);
      uploadCroppedImage(compressedBlob);
    } catch (compressError) {
      logger.error("Failed to compress image:", compressError);
      const message =
        compressError instanceof Error ? compressError.message : "Failed to prepare photo. Please try again.";
      setCompressionError(message);
      onUploadError?.(new Error(message));
    } finally {
      setIsCompressing(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedImage(null);
    revokeSelectedImageUrl();
  };

  const uploadCroppedImage = (blob: Blob) => {
    reset();
    setUploadError(null);
    const fileLike = new File([blob], "cropped.jpg", { type: blob.type || "image/jpeg" });

    uploadPhoto(fileLike, {
      onSuccess: (photoUrl) => {
        logger.info("Photo uploaded successfully");
        setUploadError(null);
        onUploadComplete?.(photoUrl);
      },
      onError: (mutationError) => {
        logger.error("Failed to upload cropped photo:", mutationError);
        const message = mutationError instanceof Error ? mutationError.message : "Upload failed";
        setUploadError(message);
        onUploadError?.(mutationError instanceof Error ? mutationError : new Error(message));
      },
    });
  };

  const handleRetryUpload = () => {
    if (!croppedImageBlob) {
      return;
    }
    setCompressionError(null);
    uploadCroppedImage(croppedImageBlob);
  };

  const displayError = compressionError ?? uploadError ?? (isError && error ? error.message : null);
  const showRetry = Boolean(croppedImageBlob && (uploadError || compressionError || isError));

  return (
    <div data-testid="photo-upload-field" className="flex flex-col items-center">
      <label htmlFor={fileInputId} className="cursor-pointer">
        <Container className="flex flex-col items-center justify-center p-8 hover:opacity-80 transition-opacity aspect-[3/4] max-w-xs w-full">
          {previewUrl ? (
            <div data-testid="preview-label" className="relative flex flex-col items-center space-y-4">
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Cropped preview"
                  data-testid="cropped-image-preview"
                  className="w-60 h-80 object-cover rounded-lg border-2 border-gray-200"
                  onError={(e) => {
                    logger.error("Failed to load preview image", e);
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-all hover:bg-black/30">
                  <Camera className="w-12 h-12 text-white opacity-0 transition-opacity hover:opacity-100" />
                </div>
              </div>
              <span className="text-lg font-medium text-gray-700">Tap to change photo</span>
            </div>
          ) : currentPhotoUrl ? (
            <div data-testid="current-photo-label" className="relative flex flex-col items-center space-y-4">
              <div className="relative">
                <img
                  src={currentPhotoUrl}
                  alt="Current profile"
                  data-testid="current-photo-preview"
                  className="w-60 h-80 object-cover rounded-lg border-2 border-gray-200"
                  onError={(e) => {
                    logger.error("Failed to load current photo", e);
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-all hover:bg-black/30">
                  <Camera className="w-12 h-12 text-white opacity-0 transition-opacity hover:opacity-100" />
                </div>
              </div>
              <span className="text-lg font-medium text-gray-700">Tap to change photo</span>
            </div>
          ) : (
            <div data-testid="upload-label" className="flex flex-col items-center space-y-4">
              {emptyStateIcon ?? <Camera className="w-16 h-16 text-gray-400" />}
              <span className="text-lg font-medium text-gray-700">Tap to upload your photo</span>
            </div>
          )}
        </Container>
      </label>

      <input
        ref={fileInputRef}
        id={fileInputId}
        data-testid="photo-file-input"
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        hidden
        className="hidden"
      />

      {isCompressing && (
        <div className="text-center py-4">
          <span className="text-pink-600 font-medium">Preparing photo...</span>
        </div>
      )}

      {isPending && (
        <div className="text-center py-4">
          <span className="text-pink-600 font-medium">Uploading...</span>
        </div>
      )}

      {displayError && (
        <div className="text-center py-4 space-y-2">
          <span data-testid="photo-upload-error" className="text-red-600 font-medium">
            {displayError}
          </span>
          {showRetry && (
            <button
              type="button"
              onClick={handleRetryUpload}
              disabled={isPending || isCompressing}
              className="block mx-auto text-sm font-medium text-pink-600 hover:text-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="photo-upload-retry"
            >
              Retry upload
            </button>
          )}
        </div>
      )}

      {showCropModal && selectedImage && (
        <PhotoCropModal image={selectedImage} onCropComplete={handleCropComplete} onCancel={handleCropCancel} />
      )}
    </div>
  );
}

export default PhotoUploadField;
